import React from 'react'
import {
  MetabaseProvider,
  StaticQuestion,
  StaticDashboard,
} from '@metabase/embedding-sdk-react'
import apis from 'browser/app/models/apis'
import './metabase-embed.scss'
import _ from 'lodash'
import { getErrorMessage } from 'shared-libs/helpers/errors'
import { IBaseProps } from '../../atoms/base-props'
import classNames from 'classnames'

/**
 * @uiComponent
 *
 * A reusable Metabase analytics embed component with configurable embedding methods.
 *
 * Authentication: Uses signed JWT tokens from your backend.
 * Each request gets a fresh JWT with embedded parameters (customer_id, facility_id, etc.)
 * No SSO or Metabase user accounts required - perfect for multi-tenant apps.
 *
 * Supports:
 * - Context parameter passing via JWT payload
 * - Automatic token refresh on param changes
 * - Responsive layout
 * - Multiple instances on the same page
 *
 * Documentation:
 * - SDK: https://www.metabase.com/docs/latest/embedding/sdk/introduction
 */

export interface IMetabaseEmbedProps extends IBaseProps {
  /** The type of Metabase content to embed: 'question' or 'dashboard' */
  resourceType?: 'question' | 'dashboard'

  /** The Metabase resource ID (question ID or dashboard ID) */
  resourceId?: number | string

  /** Context parameters to pass to Metabase (e.g., facility_id, date_range) */
  params?: Record<string, any>

  /** Locked parameters that cannot be changed by the user */
  lockedParams?: Record<string, any>

  /** Layout hint for rendering (e.g., 'kpi', 'table', 'chart', 'full') */
  layoutType?: 'kpi' | 'table' | 'chart' | 'full'

  /** Custom height (optional, defaults to container height or auto-sizing) */
  height?: string | number

  /** Custom width (optional, defaults to 100%) */
  width?: string | number

  /** Title to display above the embed (optional) */
  title?: string

  /** Whether to show border */
  showBorder?: boolean

  /** Passthrough props spread directly onto the active Metabase component (e.g. height, width, style, font) */
  styles?: Record<string, any>
}

interface IMetabaseEmbedState {
  error?: string
  metabaseUrl?: string
}

/**
 * MetabaseEmbed component with configurable embedding methods (iframe or SDK)
 */
export class MetabaseEmbed extends React.Component<IMetabaseEmbedProps, IMetabaseEmbedState> {
  private containerRef = React.createRef<HTMLDivElement>()

  static defaultProps = {
    resourceType: 'question',
    showBorder: true,
    layoutType: 'kpi',
  }

  constructor(props: IMetabaseEmbedProps) {
    super(props)
    this.state = {
      error: undefined,
      metabaseUrl: 'https://stats.withvector.com',
    }
  }

  /**
   * Fetch JWT SSO token from backend for SDK (called by Metabase SDK as needed)
   */
  private fetchRequestToken = async () => {
    try {
      const response = await apis.getMetabaseEmbedToken()
      if (!response?.token) {
        throw new Error('Failed to get JWT token from backend')
      }

      return { id: response.token, jwt: response.token }
    } catch (error) {
      console.error('[MetabaseEmbed] SDK error fetching JWT token:', error)
      this.setState({ error: getErrorMessage(error) || 'Failed to authenticate with Metabase' })
      throw error
    }
  }

  /**
   * Compute inline styles for the content wrapper to enforce height constraints.
   * StaticDashboard ignores height props, so we constrain it via the container.
   */
  private getContentStyle = (): React.CSSProperties | undefined => {
    const { height, resourceType } = this.props
    if (resourceType !== 'dashboard' || height == null) {
      return undefined
    }
    const resolvedHeight = typeof height === 'number' ? `${height}px` : height
    return {
      maxHeight: resolvedHeight,
      overflowY: 'auto',
    }
  }

  /**
   * Render SDK embedding method
   */
  private renderSDKEmbed = () => {
    const { resourceType, resourceId, params = {}, lockedParams = {}, styles = {} } = this.props
    const { metabaseUrl } = this.state

    const allParams = { ...params, ...lockedParams }

    const componentProps = {
      width: this.props.width,
      height: this.props.height,
      ...styles,
    }

    return (
      <MetabaseProvider
        authConfig={{
          metabaseInstanceUrl: metabaseUrl,
          preferredAuthMethod: 'jwt',
          fetchRequestToken: this.fetchRequestToken,
        }}
      >
        {resourceType === 'dashboard' ? (
          <StaticDashboard
            {...styles}
            dashboardId={Number(resourceId)}
            initialParameters={allParams}
          />
        ) : (
          <StaticQuestion
            {...componentProps}
            questionId={Number(resourceId)}
            initialSqlParameters={allParams}
          />
        )}
      </MetabaseProvider>
    )
  }

  public render() {
    const {
      title,
      showBorder,
      className = '',
      layoutType,
      resourceId,
    } = this.props
    const { error } = this.state

    const containerClassName = classNames(
      'metabase-embed',
      className,
      `metabase-embed--${layoutType}`,
      { 'metabase-embed--bordered': showBorder }
    )

    const containerStyle: React.CSSProperties = {}
    if (this.props.width != null) {
      containerStyle.width = typeof this.props.width === 'number' ? `${this.props.width}px` : this.props.width
    }
    if (this.props.height != null) {
      containerStyle.height = typeof this.props.height === 'number' ? `${this.props.height}px` : this.props.height
    }

    // Show error if no resource ID provided
    if (!resourceId) {
      return (
        <div className={containerClassName} style={containerStyle} ref={this.containerRef}>
          {!_.isEmpty(title) && <div className="metabase-embed__title">{title}</div>}
          <div className="metabase-embed__error">
            <p>Unable to load analytics: No resource ID provided</p>
          </div>
        </div>
      )
    }

    return (
      <div className={containerClassName} style={containerStyle} ref={this.containerRef}>
        {!_.isEmpty(title) && <div className="metabase-embed__title">{title}</div>}

        {error && (
          <div className="metabase-embed__error">
            <p>Unable to load analytics: {error}</p>
          </div>
        )}

        {!error && (
          <div className="metabase-embed__content" style={this.getContentStyle()}>
            {this.renderSDKEmbed()}
          </div>
        )}
      </div>
    )
  }
}
